import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UploadCloud, X } from "lucide-react";
import { IconaExcel, IconaPdf } from "../IconeFile";
import { useApp } from "../AppContext";
import { useAuth } from "../AuthContext";
import {
  caricaFile, getTemplates, getTemplate, applicaMapping, eliminaTemplate, rilevaTemplate,
  getModelliPdf, caricaPdfGrezzo, applicaModelloPdf, rilevaModelloPdf, eliminaModelloPdf,
} from "../api";
import { MODULI, moduloDaPercorso } from "../moduli";
import SceltaFoglioModal from "../SceltaFoglioModal";
import SceltaIntestazioneModal from "../SceltaIntestazioneModal";
import AvvisoModal from "../AvvisoModal";
import SezionePasso from "../SezionePasso";
import ApriTicketModal from "../ApriTicketModal";

// Stessa "striscia verde" usata in OrdiniMapping.jsx per "modello/template
// applicato": qui unificata anche per Distinta Base/DDT, invece del semplice
// testo con emoji usato finora — stessa conferma visiva ovunque nell'app.
const STILE_FLAG_APPLICATO = {
  color: "var(--cf-success-text)",
  background: "var(--cf-success-bg)",
  border: "1px solid var(--cf-success-border)",
  borderRadius: "6px",
  padding: "0.5rem 0.75rem",
  fontWeight: 600,
};

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const modulo = moduloDaPercorso(location.pathname);
  const percorsoBase = MODULI[modulo].percorsoBase;
  const {
    tracciato, fileCaricato, setFileCaricato, mapping,
    setTemplateAttivo, setMapping, setColonneNascoste, righeMappate, setRigheMappate,
    // Questi tre vivono nel context (non come stato locale della pagina)
    // apposta: Home si smonta quando si passa a Mapping/Revisione (route
    // diverse), altrimenti "Torna indietro" da Revisione farebbe perdere la
    // conferma di cosa era stato applicato — vedi il commento in AppContext.js.
    pdfCaricato, setPdfCaricato, modelloPdfApplicato, setModelloPdfApplicato,
    templateApplicato, setTemplateApplicato,
    lottoPdf, setLottoPdf,
    resetFlusso,
  } = useApp();
  // Chi non ha il permesso di mappare vede solo applicare un template/modello
  // già esistente + calcolare l'anteprima: niente editor di mapping, niente
  // creare/modificare/eliminare template o modelli PDF. Solo nascondere
  // l'interfaccia non basterebbe da solo: gli endpoint di scrittura restano
  // comunque protetti lato server (routes/api.js, routes/pdfModelli.js).
  const { puoMappare, azienda } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [templateSelezionato, setTemplateSelezionato] = useState("");
  const [suggerimentoTemplate, setSuggerimentoTemplate] = useState(null); // { id, cliente, punteggio } | null
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState("");
  const [trascinamento, setTrascinamento] = useState(false);
  const [avanzamento, setAvanzamento] = useState(""); // "Leggo i PDF: 3 di 42" durante un caricamento multiplo
  const [modalitaMultipla, setModalitaMultipla] = useState(false); // true: i file scelti vanno in coda, si avvia con "Vai"
  const [codaMultipla, setCodaMultipla] = useState([]); // File[] in attesa di "Vai"
  const inputRef = useRef(null);
  // Byte originali dell'Excel caricato — vedi lo stesso stato/stesso motivo
  // in OrdiniMapping.jsx: serve solo per allegarlo a un eventuale ticket di
  // assistenza (ApriTicketModal.jsx), perché /api/upload non salva mai i
  // byte originali su disco (solo headers/righe già interpretati).
  const [fileOriginaleGrezzo, setFileOriginaleGrezzo] = useState(null);
  const [mostraTicketPdf, setMostraTicketPdf] = useState(false);
  const [mostraTicketTemplate, setMostraTicketTemplate] = useState(false);
  // true dopo un invio riuscito: disabilita il pulsante "Apri ticket" per
  // questo stesso file, così non si rischia di aprirne un secondo per
  // errore riaprendo la modale. Azzerati insieme al resto ad ogni nuovo
  // file caricato — vedi processaFile.
  const [ticketPdfInviato, setTicketPdfInviato] = useState(false);
  const [ticketTemplateInviato, setTicketTemplateInviato] = useState(false);

  // Wizard "a fisarmonica" (solo per chi NON può mappare, vedi "wizard" più
  // sotto): quale passo già completato l'utente ha riaperto a mano per
  // rivederlo/cambiarlo. Un solo passo alla volta; null = nessuno riaperto,
  // si segue lo stato naturale (passo attivo aperto, completati prima
  // compressi, quelli dopo bloccati) — vedi statoDiPasso più sotto.
  const [stepRiaperto, setStepRiaperto] = useState(null);

  // Un PDF non ha colonne vere: prima di poterlo importare serve un
  // "modello" già calibrato per quel layout (vedi CalibrazionePdf.jsx). Il
  // PDF viene caricato "grezzo" (i suoi byte, sul server) appena scelto —
  // non solo tenuto in memoria come File — così si può subito controllare
  // se un modello salvato lo riconosce, senza dover ritrasmettere il file
  // più volte per ogni azione successiva. (pdfCaricato stesso vive nel
  // context, vedi sopra.)
  const [modelliPdf, setModelliPdf] = useState([]);
  const [modelloPdfScelto, setModelloPdfScelto] = useState("");
  const [suggerimentoModello, setSuggerimentoModello] = useState(null); // { id, cliente, punteggio } | null

  // Elenco persistente in fondo alla pagina (come i template Excel), per
  // poter modificare/eliminare un modello anche senza aver appena caricato
  // un PDF.
  const [modelliPdfSalvati, setModelliPdfSalvati] = useState([]);
  const [modelloPdfDaModificare, setModelloPdfDaModificare] = useState(null);
  const inputModificaModelloRef = useRef(null);

  // Stesso principio del "file di riferimento" chiesto per modificare un
  // modello PDF, applicato ai template (Excel/DDT): quale template stiamo
  // per aprire in modifica, in attesa che l'utente scelga il file da usare
  // come riferimento per vedere i nomi reali delle colonne.
  const [templateDaModificare, setTemplateDaModificare] = useState(null);
  const inputModificaTemplateRef = useRef(null);

  // Alcuni clienti mettono i dati veri non nel primo foglio di un Excel (es.
  // il primo è un riepilogo): con più fogli il backend risponde chiedendo di
  // scegliere prima di procedere (vedi routes/api.js /upload), invece di
  // indovinare il primo foglio. "perModificaTemplate" ricorda quale dei due
  // flussi (caricamento normale o "Modifica" su un template salvato) ha
  // chiesto la scelta, per sapere come continuare una volta scelto il foglio.
  const [fileInAttesaFoglio, setFileInAttesaFoglio] = useState(null); // { file, fogli, nomeOriginale, perModificaTemplate } | null

  // Stessa idea, per quando la prima riga del foglio scelto non sembra
  // l'intestazione vera (vedi routes/api.js /upload, euristica sul numero di
  // celle valorizzate delle prime righe).
  const [fileInAttesaIntestazione, setFileInAttesaIntestazione] = useState(null); // { file, foglio, righeAnteprima, nomeOriginale, perModificaTemplate } | null

  // Conferma di cosa è stato applicato per ultimo (modelloPdfApplicato/
  // templateApplicato, dal context): resta visibile invece di far sparire la
  // sezione (modello PDF) o saltare subito alla Revisione (template).
  // Azzerati insieme ad ogni nuovo file o rimozione, perché diventerebbero
  // riferiti a un file che non c'è più.
  function azzeraConfermeApplicazione() {
    setModelloPdfApplicato(null);
    setTemplateApplicato(null);
  }

  // Dipende da "modulo" (non solo mount): Home è lo stesso componente per
  // ogni modulo di import, se si passa da Distinta Base a DDT senza smontare
  // la pagina (es. link diretto) l'elenco template va ricaricato per il
  // nuovo modulo.
  useEffect(() => { ricaricaTemplates(); }, [modulo]);
  // I modelli PDF sono legati al modulo (diba/ddt/ordini): stesso PDF di un
  // cliente, ma letto in modo diverso a seconda del tracciato di
  // destinazione, quindi non condivisi tra le tab — vanno ricaricati anche
  // loro ad ogni cambio di modulo, come i template.
  useEffect(() => { ricaricaModelliPdfSalvati(); }, [modulo]);
  // "modelliPdf" (l'elenco nella tendina "Usa un modello PDF già calibrato")
  // resta invece locale e vuoto ad ogni montaggio: se si torna qui da
  // Revisione con un pdfCaricato già presente (dal context, vedi sopra) va
  // ricaricato subito, altrimenti la tendina risulterebbe vuota anche se il
  // modello applicato è ancora mostrato correttamente (modelloPdfApplicato).
  useEffect(() => {
    if (pdfCaricato && !pdfCaricato.avviso) {
      getModelliPdf(modulo).then(setModelliPdf).catch((e) => setErrore(e.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfCaricato, modulo]);

  function ricaricaTemplates() {
    getTemplates(modulo).then(setTemplates).catch((e) => setErrore(e.message));
  }

  function ricaricaModelliPdfSalvati() {
    getModelliPdf(modulo).then(setModelliPdfSalvati).catch((e) => setErrore(e.message));
  }

  // Tra i template salvati, quale corrisponde meglio alle colonne di
  // questo file — stessa idea del riconoscimento automatico già fatto per i
  // PDF (vedi rilevaModelloPdf più sotto), ma qui il confronto è sui NOMI
  // delle colonne sorgente invece che sulla posizione: non serve solo per i
  // file Excel caricati direttamente, vale anche per un file che arriva da
  // un modello PDF appena applicato (stessa forma {headers, ...}).
  async function proponiTemplatePerHeaders(headers) {
    setSuggerimentoTemplate(null);
    try {
      const { migliore } = await rilevaTemplate(headers, modulo);
      if (migliore && migliore.punteggio >= 0.8) {
        setSuggerimentoTemplate(migliore);
        setTemplateSelezionato(migliore.id);
        return;
      }
    } catch (e) {
      // Riconoscimento best-effort: se fallisce si torna semplicemente alla
      // scelta manuale, senza bloccare il resto del flusso.
    }
    setTemplateSelezionato("");
  }

  // Caricamento multiplo (solo PDF): ogni PDF viene letto e riconosciuto, si
  // sceglie il modello che li riconosce (quello più votato), quelli che non
  // combaciano restano fuori con il motivo. Poi modello e template si
  // applicano a tutti, e in Revisione i dati da mettere a mano valgono per
  // tutti: le righe di tutti i PDF finiscono in un'unica tabella, e
  // l'esportazione le divide da sola in un file per ogni articolo/variante.
  async function processaLotto(files) {
    const elenco = Array.from(files || []);
    if (elenco.length === 0) return;
    if (!elenco.every((f) => /\.pdf$/i.test(f.name))) {
      setErrore("Il caricamento multiplo funziona solo con file PDF: scegli solo PDF, oppure un file Excel alla volta.");
      return;
    }
    setErrore("");
    setPdfCaricato(null);
    setSuggerimentoModello(null);
    setSuggerimentoTemplate(null);
    setTicketPdfInviato(false);
    setTicketTemplateInviato(false);
    azzeraConfermeApplicazione();
    setCaricamento(true);
    try {
      const modelli = await getModelliPdf(modulo);
      const letti = [];
      const scartati = [];
      for (let i = 0; i < elenco.length; i++) {
        setAvanzamento(`Leggo i PDF: ${i + 1} di ${elenco.length}`);
        try {
          const grezzo = await caricaPdfGrezzo(elenco[i]);
          if (grezzo.avviso) {
            scartati.push({ nomeOriginale: elenco[i].name, motivo: "nessun testo leggibile (probabile scansione)" });
            continue;
          }
          const { candidati } = await rilevaModelloPdf(grezzo.pdfId, modulo);
          letti.push({ grezzo, candidati: (candidati || []).filter((c) => modelli.some((m) => m.id === c.id)) });
        } catch (err) {
          scartati.push({ nomeOriginale: elenco[i].name, motivo: err.message });
        }
      }
      const migliorePerPdf = (l) => [...l.candidati].sort((a, b) => b.punteggio - a.punteggio)[0];
      const voti = new Map();
      for (const l of letti) {
        const m = migliorePerPdf(l);
        if (m && m.punteggio >= 0.8) voti.set(m.id, (voti.get(m.id) || 0) + 1);
      }
      const idModello = [...voti.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
      resetFlusso();
      setFileOriginaleGrezzo(null);
      if (!idModello) {
        setModelliPdf(modelli);
        setErrore("Nessuno dei PDF caricati è stato riconosciuto da un modello: controlla che siano dello stesso cliente, o calibra un modello.");
        return;
      }
      // Chi non può mappare accetta solo il 100%, gli altri dall'80%: i PDF
      // sotto soglia restano fuori, non bloccano tutto il gruppo.
      const soglia = puoMappare ? 0.8 : 1;
      const membri = [];
      for (const l of letti) {
        const c = l.candidati.find((x) => x.id === idModello);
        if (c && c.punteggio >= soglia) membri.push({ ...l, punteggio: c.punteggio });
        else scartati.push({
          nomeOriginale: l.grezzo.nomeOriginale,
          motivo: c ? `layout riconosciuto solo al ${Math.round(c.punteggio * 100)}%` : "layout non riconosciuto",
        });
      }
      const cliente = modelli.find((m) => m.id === idModello)?.cliente || "";
      setModelliPdf(modelli);
      setModelloPdfScelto(idModello);
      setPdfCaricato(membri[0].grezzo);
      setSuggerimentoModello({ id: idModello, cliente, punteggio: Math.min(...membri.map((m) => m.punteggio)) });
      setLottoPdf({
        extra: membri.slice(1).map((m) => ({ pdfId: m.grezzo.pdfId, nomeOriginale: m.grezzo.nomeOriginale })),
        scartati,
        file: null,
      });
    } catch (err) {
      setErrore(err.message);
    } finally {
      setAvanzamento("");
      setCaricamento(false);
    }
  }

  async function processaFile(file) {
    if (!file) return;
    setLottoPdf(null);
    setErrore("");
    setPdfCaricato(null);
    setSuggerimentoModello(null);
    setSuggerimentoTemplate(null);
    setTicketPdfInviato(false);
    setTicketTemplateInviato(false);
    azzeraConfermeApplicazione();

    if (/\.pdf$/i.test(file.name)) {
      setCaricamento(true);
      try {
        const grezzo = await caricaPdfGrezzo(file);
        // resetFlusso() PRIMA di impostare il nuovo pdfCaricato: azzera
        // anche pdfCaricato/modelloPdfApplicato/templateApplicato (ora nel
        // context, vedi AppContext.js) — nell'ordine inverso il valore
        // appena impostato verrebbe subito azzerato di nuovo.
        resetFlusso();
        setFileOriginaleGrezzo(null);
        setPdfCaricato(grezzo);
        if (grezzo.avviso) {
          // PDF senza testo estraibile (probabile scansione): avviso subito,
          // niente da riconoscere/proporre.
          setErrore(grezzo.avviso);
          setModelliPdf([]);
          return;
        }
        const [modelli, rilevamento] = await Promise.all([
          getModelliPdf(modulo),
          rilevaModelloPdf(grezzo.pdfId, modulo).catch(() => ({ migliore: null })),
        ]);
        setModelliPdf(modelli);
        const trovato = rilevamento.migliore;
        if (trovato && trovato.punteggio >= 0.8 && modelli.some((m) => m.id === trovato.id)) {
          setSuggerimentoModello(trovato);
          setModelloPdfScelto(trovato.id);
        } else {
          setModelloPdfScelto(modelli[0]?.id || "");
        }
      } catch (err) {
        setErrore(err.message);
      } finally {
        setCaricamento(false);
      }
      return;
    }

    // Niente riuso di templateApplicato?.impostazioniFile qui: in questa
    // pagina un template si applica SEMPRE dopo aver già caricato un file
    // (la sezione "Template cliente" compare solo con un file caricato), non
    // prima come nel caso "so già chi è il cliente" di OrdiniMapping.jsx —
    // quindi ogni nuovo file arrivato qui è per definizione un caricamento
    // "a freddo": si affida al riconoscimento automatico del backend (vedi
    // provaRiconoscimentoAutomatico in routes/api.js), non alle impostazioni
    // di un template legato al file precedente (che, tra l'altro, sarebbero
    // comunque quelle di un render fa: azzeraConfermeApplicazione qui sopra
    // non le ha ancora azzerate davvero in questa stessa chiamata). L'unico
    // punto dove riapplicare le impostazioniFile di un template già noto
    // PRIMA del caricamento è "Modifica" nell'elenco template
    // (eseguiModificaTemplateConFile), che le rilegge sempre fresche dal
    // server invece di fidarsi dello stato locale.
    await caricaFileExcel(file, {});
  }

  async function caricaFileExcel(file, opzioni = {}) {
    setCaricamento(true);
    setRigheMappate(null);
    setTemplateAttivo(null);
    setMapping({});
    setColonneNascoste([]);
    try {
      const risposta = await caricaFile(file, opzioni, modulo);
      if (risposta.serveSceltaFoglio) {
        setFileInAttesaFoglio({ file, fogli: risposta.fogli, nomeOriginale: risposta.nomeOriginale, perModificaTemplate: false });
        return;
      }
      if (risposta.serveSceltaRigaIntestazioni) {
        setFileInAttesaIntestazione({
          file, foglio: opzioni.foglio, righeAnteprima: risposta.righeAnteprima,
          nomeOriginale: risposta.nomeOriginale, perModificaTemplate: false,
        });
        return;
      }
      if (risposta.templateRiconosciuto) {
        // Caricamento "a freddo" (nessun template scelto prima del file): il
        // backend ha comunque riconosciuto da solo un template salvato
        // (stesso foglio/riga già noti, colonne combacianti — vedi
        // provaRiconoscimentoAutomatico in routes/api.js). Lo applichiamo
        // subito, come se l'utente lo avesse scelto dalla tendina, invece di
        // lasciare il file caricato senza nessun template.
        const template = await getTemplate(risposta.templateRiconosciuto.id, modulo);
        setTemplateAttivo(template);
        setMapping(template.mapping);
        setColonneNascoste(template.colonneNascoste || []);
        setFileCaricato({ ...risposta, impostazioniFile: template.impostazioniFile });
        setFileOriginaleGrezzo(file);
        const { righe } = await applicaMapping({ fileId: risposta.fileId, mapping: template.mapping, modulo });
        setRigheMappate(righe);
        setTemplateApplicato(template);
        return;
      }
      // Foglio/riga REALMENTE usati (risposta.nomeFoglio/rigaIntestazioni...,
      // vedi routes/api.js): con un riconoscimento solo "strutturale" (nome
      // foglio combaciante ma colonne non più al 100%, es. una colonna
      // rinominata) il server sceglie foglio/riga da solo SENZA passare da
      // "opzioni" — usare "opzioni" qui (com'era prima) lascerebbe
      // fileCaricato.impostazioniFile vuoto in quel caso, e un successivo
      // salvataggio del template perderebbe foglio/riga. Il fallback su
      // "opzioni" resta per i PDF (dove risposta non li valorizza) e per
      // sicurezza.
      const impostazioniRisolte = {
        foglio: risposta.nomeFoglio ?? opzioni.foglio,
        rigaIntestazioni: Number.isInteger(risposta.rigaIntestazioni) ? risposta.rigaIntestazioni : opzioni.rigaIntestazioni,
        rigaIntestazioniSecondaria: risposta.rigaIntestazioniSecondaria ?? opzioni.rigaIntestazioniSecondaria ?? null,
      };
      setFileCaricato({ ...risposta, impostazioniFile: impostazioniRisolte });
      setFileOriginaleGrezzo(file);
      await proponiTemplatePerHeaders(risposta.headers);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setCaricamento(false);
    }
  }

  async function procediConFoglio(foglio) {
    if (!fileInAttesaFoglio) return;
    const { file, perModificaTemplate } = fileInAttesaFoglio;
    setFileInAttesaFoglio(null);
    if (perModificaTemplate) {
      await eseguiModificaTemplateConFile(file, { foglio });
    } else {
      await caricaFileExcel(file, { foglio });
    }
  }

  function annullaSceltaFoglio() {
    setFileInAttesaFoglio(null);
    setTemplateDaModificare(null);
  }

  async function procediConIntestazione(rigaIntestazioni, rigaIntestazioniSecondaria) {
    if (!fileInAttesaIntestazione) return;
    const { file, foglio, perModificaTemplate } = fileInAttesaIntestazione;
    setFileInAttesaIntestazione(null);
    if (perModificaTemplate) {
      await eseguiModificaTemplateConFile(file, { foglio, rigaIntestazioni, rigaIntestazioniSecondaria });
    } else {
      await caricaFileExcel(file, { foglio, rigaIntestazioni, rigaIntestazioniSecondaria });
    }
  }

  function annullaSceltaIntestazione() {
    setFileInAttesaIntestazione(null);
    setTemplateDaModificare(null);
  }

  async function usaModelloPdfScelto() {
    // modelloPdfSceltoEffettivo (non la variabile di stato grezza): dopo un
    // ritorno da Revisione la tendina può mostrare il modello già applicato
    // solo grazie al fallback su modelloPdfApplicato — vedi il commento
    // vicino ad applicaModelloBloccato più sotto.
    if (!pdfCaricato || !modelloPdfSceltoEffettivo) return;
    setErrore("");
    setCaricamento(true);
    try {
      const risposta = await applicaModelloPdf(pdfCaricato.pdfId, modelloPdfSceltoEffettivo);
      setFileCaricato(risposta);
      // Caricamento multiplo: stesso modello su tutti gli altri PDF. Un PDF
      // che non produce righe o ha colonne diverse dal primo resta fuori
      // (con il motivo), così le righe mappate dopo hanno tutte lo stesso schema.
      if (lottoPdf?.extra?.length) {
        const file = [];
        const scartati = lottoPdf.scartati.filter((s) => !s.dopoApplicazione);
        for (let i = 0; i < lottoPdf.extra.length; i++) {
          const e = lottoPdf.extra[i];
          setAvanzamento(`Applico il modello: ${i + 2} di ${lottoPdf.extra.length + 1}`);
          try {
            const r = await applicaModelloPdf(e.pdfId, modelloPdfSceltoEffettivo);
            if (r.headers.join("|") !== risposta.headers.join("|")) {
              scartati.push({ nomeOriginale: e.nomeOriginale, motivo: "colonne diverse dagli altri PDF", dopoApplicazione: true });
            } else if (!r.totaleRighe) {
              scartati.push({ nomeOriginale: e.nomeOriginale, motivo: "nessuna riga letta", dopoApplicazione: true });
            } else {
              file.push(r);
            }
          } catch (err) {
            scartati.push({ nomeOriginale: e.nomeOriginale, motivo: err.message, dopoApplicazione: true });
          }
        }
        setLottoPdf({ ...lottoPdf, scartati, file });
      }
      // A differenza di prima, NON si nasconde più questa sezione: resta
      // visibile con una conferma di quale modello è stato applicato,
      // invece di far sparire tutto e lasciare l'utente senza sapere cosa
      // ha appena scelto. "Applica un altro modello" più sotto la fa
      // ricomparire in modalità scelta, se serve cambiare idea.
      setModelloPdfApplicato(modelliPdf.find((m) => m.id === modelloPdfSceltoEffettivo) || { id: modelloPdfSceltoEffettivo, cliente: "" });
      setTemplateApplicato(null); // il file sotto è cambiato: un template applicato prima non è più valido
      // suggerimentoModello NON si azzera più qui (si azzerava, causando un
      // bug: dopo un'applicazione riuscita il messaggio "non riconosciuto"
      // ricompariva subito perché non c'era più nessun suggerimento a
      // dimostrare il contrario) — resta valorizzato e il banner
      // "riconosciuto" si nasconde con modelloPdfApplicato più sotto.
      await proponiTemplatePerHeaders(risposta.headers);
      if (risposta.avvisoLayout) setErrore(risposta.avvisoLayout);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setAvanzamento("");
      setCaricamento(false);
    }
  }

  // Cambiare la tendina non deve mai "incastrare" chi non può mappare in un
  // vicolo cieco: prima si verificava SOLO il modello proposto in automatico
  // all'apertura del PDF — scegliere un altro (anche tornando poi su quello
  // giusto) restava "non riconosciuto" per sempre, senza modo di sbloccarsi
  // se non ricaricando tutto da capo. Qui invece si richiede al server un
  // punteggio fresco per QUALUNQUE modello scelto (rilevaModelloPdf ora
  // restituisce il punteggio di ogni candidato, non solo il migliore),
  // aggiornando "suggerimentoModello" di conseguenza: applicaModelloBloccato
  // si ricalcola sempre sulla scelta ATTUALE, mai su quella di un attimo fa.
  async function onCambiaModelloPdfScelto(nuovoId) {
    setModelloPdfScelto(nuovoId);
    if (nuovoId !== modelloPdfApplicato?.id) setModelloPdfApplicato(null);
    if (!nuovoId || !pdfCaricato) { setSuggerimentoModello(null); return; }
    try {
      const { candidati } = await rilevaModelloPdf(pdfCaricato.pdfId, modulo);
      const trovato = (candidati || []).find((c) => c.id === nuovoId) || null;
      // Caricamento multiplo: vale il punteggio peggiore tra tutti i PDF.
      if (trovato && lottoPdf?.extra?.length) {
        for (const e of lottoPdf.extra) {
          const r = await rilevaModelloPdf(e.pdfId, modulo);
          const c = (r.candidati || []).find((x) => x.id === nuovoId);
          trovato.punteggio = Math.min(trovato.punteggio, c ? c.punteggio : 0);
        }
      }
      setSuggerimentoModello(trovato);
    } catch (e) {
      setSuggerimentoModello(null);
    }
  }

  function calibraNuovoModelloPdf() {
    if (!pdfCaricato) return;
    navigate(`${percorsoBase}/calibrazione-pdf?pdfId=${pdfCaricato.pdfId}`);
  }

  function modificaModelloPdfScelto() {
    if (!pdfCaricato || !modelloPdfSceltoEffettivo) return;
    navigate(`${percorsoBase}/calibrazione-pdf?pdfId=${pdfCaricato.pdfId}&modelloId=${modelloPdfSceltoEffettivo}`);
  }

  function chiediFilePerModificaModello(modelloId) {
    setErrore("");
    setModelloPdfDaModificare(modelloId);
    inputModificaModelloRef.current?.click();
  }

  async function onFilePerModificaModello(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !modelloPdfDaModificare) return;
    setErrore("");
    setCaricamento(true);
    try {
      const grezzo = await caricaPdfGrezzo(file);
      navigate(`${percorsoBase}/calibrazione-pdf?pdfId=${grezzo.pdfId}&modelloId=${modelloPdfDaModificare}`);
    } catch (err) {
      setErrore(err.message);
      setCaricamento(false);
    }
  }

  async function eliminaModelloPdfSalvato(id) {
    if (!window.confirm("Eliminare questo modello PDF? I PDF futuri con questo layout non verranno più riconosciuti automaticamente.")) return;
    try {
      await eliminaModelloPdf(id);
      ricaricaModelliPdfSalvati();
    } catch (err) {
      setErrore(err.message);
    }
  }

  // Caricamento multiplo: i PDF si accumulano in una coda (anche uno per
  // volta, in momenti diversi) e restano solo in memoria nel browser — niente
  // viene inviato né riconosciuto finché non si preme "Vai".
  function aggiungiAllaCoda(files) {
    const nuovi = Array.from(files || []);
    if (nuovi.length === 0) return;
    if (!nuovi.every((f) => /\.pdf$/i.test(f.name))) {
      setErrore("Il caricamento multiplo funziona solo con file PDF: scegli solo PDF, oppure un file Excel alla volta.");
      return;
    }
    setErrore("");
    setCodaMultipla((prima) => [
      ...prima,
      ...nuovi.filter((n) => !prima.some((p) => p.name === n.name && p.size === n.size)),
    ]);
  }

  function attivaCaricamentoMultiplo() {
    setModalitaMultipla(true);
    setErrore("");
  }

  function annullaCaricamentoMultiplo() {
    setModalitaMultipla(false);
    setCodaMultipla([]);
  }

  async function avviaCaricamentoMultiplo() {
    const coda = codaMultipla;
    setModalitaMultipla(false);
    setCodaMultipla([]);
    await processaLotto(coda);
  }

  function caricaFiles(files) {
    if (modalitaMultipla || (files && files.length > 1)) {
      setModalitaMultipla(true);
      aggiungiAllaCoda(files);
    } else {
      processaFile(files?.[0]);
    }
  }

  function onFileSelezionato(e) {
    caricaFiles(Array.from(e.target.files));
    e.target.value = ""; // permette di ricaricare lo stesso file una seconda volta
  }

  function onDrop(e) {
    e.preventDefault();
    setTrascinamento(false);
    if (caricamento) return;
    caricaFiles(Array.from(e.dataTransfer.files || []));
  }

  async function usaTemplateEsistente() {
    // templateSelezionatoEffettivo (non la variabile di stato grezza): dopo
    // un ritorno da Revisione la tendina può mostrare il template già
    // applicato solo grazie al fallback su templateApplicato — vedi il
    // commento vicino ad applicaTemplateBloccato più sotto.
    if (!templateSelezionatoEffettivo || !fileCaricato) return;
    setErrore("");
    setCaricamento(true);
    try {
      const template = await getTemplate(templateSelezionatoEffettivo, modulo);
      setTemplateAttivo(template);
      setMapping(template.mapping);
      setColonneNascoste(template.colonneNascoste || []);
      const { righe } = await applicaMapping({
        fileId: fileCaricato.fileId,
        fileIds: lottoPdf?.file ? [fileCaricato.fileId, ...lottoPdf.file.map((f) => f.fileId)] : undefined,
        mapping: template.mapping,
        modulo,
      });
      setRigheMappate(righe);
      // Non si salta più dritti alla Revisione: resta qui una conferma di
      // cosa è stato applicato (template + righe pronte), con un pulsante
      // per proseguire quando l'utente ha controllato.
      setTemplateApplicato(template);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setCaricamento(false);
    }
  }

  // Cambiare la tendina non deve mai "incastrare" chi non può mappare in un
  // vicolo cieco: prima si verificava SOLO il template proposto in automatico
  // all'apertura del file — scegliere un altro (anche tornando poi su quello
  // giusto) restava "non riconosciuto" per sempre, senza modo di sbloccarsi
  // se non ricaricando tutto da capo. Qui invece si richiede al server un
  // punteggio fresco per QUALUNQUE template scelto (rilevaTemplate ora
  // restituisce il punteggio di ogni candidato, non solo il migliore),
  // aggiornando "suggerimentoTemplate" di conseguenza: applicaTemplateBloccato
  // si ricalcola sempre sulla scelta ATTUALE, mai su quella di un attimo fa.
  async function onCambiaTemplateSelezionato(nuovoId) {
    setTemplateSelezionato(nuovoId);
    if (nuovoId !== templateApplicato?.id) setTemplateApplicato(null);
    if (!nuovoId || !fileCaricato) { setSuggerimentoTemplate(null); return; }
    try {
      const { candidati } = await rilevaTemplate(fileCaricato.headers, modulo);
      setSuggerimentoTemplate((candidati || []).find((c) => c.id === nuovoId) || null);
    } catch (e) {
      setSuggerimentoTemplate(null);
    }
  }

  function creaNuovoMapping() {
    setTemplateAttivo(null);
    setMapping({});
    setColonneNascoste([]);
    navigate(`${percorsoBase}/mapping`);
  }

  // Apre il mapping di un template esistente per modificarlo. Funziona anche
  // SENZA un file cliente caricato (es. appena entrati, o un template
  // salvato la volta scorsa che si vuole solo rifinire): in quel caso
  // Mapping.jsx passa in una modalità "senza file" dove le colonne sorgente
  // si scrivono a mano invece di sceglierle da un elenco, e anteprima/
  // estrazione restano disattivate finché non si carica davvero un file.
  async function modificaTemplate(id) {
    if (!id) return;
    setErrore("");
    setCaricamento(true);
    try {
      const template = await getTemplate(id, modulo);
      setTemplateAttivo(template);
      setMapping(template.mapping);
      setColonneNascoste(template.colonneNascoste || []);
      navigate(`${percorsoBase}/mapping`);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setCaricamento(false);
    }
  }

  // A differenza di "modificaTemplate" (usato quando un file è già caricato,
  // dal passo 2), "Modifica" nell'elenco dei template salvati poteva finora
  // aprire il mapping SENZA nessun file di riferimento: le colonne sorgente
  // si scrivevano a mano, a occhio, senza sapere se il nome scritto
  // corrispondeva davvero a una colonna del file del cliente. Stessa
  // logica già usata per i modelli PDF (chiediFilePerModificaModello): si
  // chiede sempre un file prima di entrare nel mapping.
  function chiediFilePerModificaTemplate(id) {
    setErrore("");
    setTemplateDaModificare(id);
    inputModificaTemplateRef.current?.click();
  }

  async function onFilePerModificaTemplate(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !templateDaModificare) return;
    await eseguiModificaTemplateConFile(file);
  }

  async function eseguiModificaTemplateConFile(file, opzioni = {}) {
    setErrore("");
    setCaricamento(true);
    try {
      // Il template va letto PRIMA (non più in Promise.all con caricaFile):
      // solo così si conoscono già le sue impostazioniFile salvate (foglio +
      // riga/righe intestazione dell'ultima volta) da usare come default per
      // QUESTO caricamento — un giro di rete in più, ma è l'unico modo per
      // non richiedere di nuovo SceltaFoglioModal/SceltaIntestazioneModal
      // quando il file ha la stessa struttura di sempre per questo cliente.
      // Una scelta esplicita già in "opzioni" (es. un ritentativo dopo un
      // modal) vince comunque su quella salvata.
      const template = await getTemplate(templateDaModificare, modulo);
      const opzioniEffettive = { ...template.impostazioniFile, ...opzioni };
      const risposta = await caricaFile(file, opzioniEffettive);
      if (risposta.serveSceltaFoglio) {
        setFileInAttesaFoglio({ file, fogli: risposta.fogli, nomeOriginale: risposta.nomeOriginale, perModificaTemplate: true });
        return;
      }
      if (risposta.serveSceltaRigaIntestazioni) {
        setFileInAttesaIntestazione({
          file, foglio: opzioniEffettive.foglio, righeAnteprima: risposta.righeAnteprima,
          nomeOriginale: risposta.nomeOriginale, perModificaTemplate: true,
        });
        return;
      }
      setTemplateAttivo(template);
      setMapping(template.mapping);
      setColonneNascoste(template.colonneNascoste || []);
      setFileCaricato({ ...risposta, impostazioniFile: opzioniEffettive });
      navigate(`${percorsoBase}/mapping`);
      setTemplateDaModificare(null);
    } catch (err) {
      setErrore(err.message);
      setTemplateDaModificare(null);
    } finally {
      setCaricamento(false);
    }
  }

  async function rimuoviTemplate(id) {
    if (!window.confirm("Eliminare questo template cliente?")) return;
    try {
      await eliminaTemplate(id, modulo);
      ricaricaTemplates();
    } catch (err) {
      setErrore(err.message);
    }
  }

  // Toglie un solo PDF dal caricamento (o l'unico PDF, se non è un lotto).
  // Se era il primo, il successivo prende il suo posto. Il modello già
  // applicato non vale più (il gruppo di file è cambiato): va riapplicato.
  function rimuoviPdfDalLotto(pdfId) {
    const tutti = lottoPdf ? [pdfCaricato, ...lottoPdf.extra] : [pdfCaricato];
    const restanti = tutti.filter((p) => p.pdfId !== pdfId);
    azzeraConfermeApplicazione();
    setFileCaricato(null);
    setRigheMappate(null);
    if (restanti.length === 0) {
      setPdfCaricato(null);
      setLottoPdf(null);
      setSuggerimentoModello(null);
      return;
    }
    setPdfCaricato(restanti[0]);
    setLottoPdf(lottoPdf ? { ...lottoPdf, extra: restanti.slice(1), file: null } : null);
  }

  function rimuoviFileCaricato() {
    resetFlusso();
    setFileOriginaleGrezzo(null);
    setTemplateSelezionato("");
    setPdfCaricato(null);
    setSuggerimentoModello(null);
    azzeraConfermeApplicazione();
  }

  // Anteprima del risultato subito dopo aver applicato un template — utile
  // soprattutto per chi non può modificare il mapping (vedi puoMappare): può
  // comunque controllare a colpo d'occhio se il template scelto è quello
  // giusto, senza dover per forza entrare in Revisione. Stesse colonne
  // "con valore" già usate in Mapping.jsx.
  const colonneAnteprima = tracciato
    ? tracciato.colonne.filter((c) => {
        const r = mapping[c.chiave];
        return r && r.tipo !== "manuale";
      })
    : [];

  // Chi non può mappare non può nemmeno correggere un modello/template
  // sbagliato: applicarne uno riconosciuto solo in parte (o scelto a mano,
  // alla cieca) rischierebbe di mappare male i dati senza che se ne accorga.
  // Per lui "Applica" resta bloccato finché il riconoscimento automatico non
  // è al 100% E la scelta in tendina coincide ancora con quel suggerimento
  // (cambiando manualmente la tendina, o quando è solo parziale/assente, il
  // suggerimento si azzera — vedi gli onChange qui sopra — e si ricade qui).
  //
  // modelloPdfScelto/templateSelezionato sono stato locale della pagina: si
  // azzerano quando Home si smonta (si passa a Mapping/Revisione, route
  // diverse) e rimonta, anche se modelloPdfApplicato/templateApplicato (nel
  // context) sono rimasti quelli di prima. Senza questo fallback, tornando
  // da Revisione la tendina risulterebbe vuota e riselezionare — anche lo
  // STESSO cliente già applicato — verrebbe trattato come "mai verificato" e
  // bloccato per chi non può mappare: un bug vero, non solo estetico.
  const modelloPdfSceltoEffettivo = modelloPdfScelto || modelloPdfApplicato?.id || "";
  const templateSelezionatoEffettivo = templateSelezionato || templateApplicato?.id || "";
  const applicaModelloBloccato = !puoMappare && !(
    (suggerimentoModello && suggerimentoModello.punteggio >= 1 && suggerimentoModello.id === modelloPdfSceltoEffettivo)
    || (modelloPdfApplicato && modelloPdfApplicato.id === modelloPdfSceltoEffettivo)
  );
  const applicaTemplateBloccato = !puoMappare && !(
    (suggerimentoTemplate && suggerimentoTemplate.punteggio >= 1 && suggerimentoTemplate.id === templateSelezionatoEffettivo)
    || (templateApplicato && templateApplicato.id === templateSelezionatoEffettivo)
  );

  // Wizard guidato "a fisarmonica", SOLO per chi non può mappare: chi può
  // mappare deve poter saltare liberamente tra le sezioni (rifare
  // un'estrazione, provare un mapping diverso...), un percorso rigido lo
  // intralcerebbe soltanto — per lui la pagina resta esattamente come è
  // sempre stata (vedi "wizard" più sotto, usato per decidere come
  // avvolgere ogni sezione nel render).
  const wizard = !puoMappare;
  const passiWizard = wizard
    ? [
        {
          chiave: "file",
          titolo: "Carica il file del cliente",
          completo: !!fileCaricato || !!(pdfCaricato && !pdfCaricato.avviso),
          riepilogo: fileCaricato
            ? `${fileCaricato.nomeOriginale} — ${fileCaricato.totaleRighe} righe`
            : (pdfCaricato ? pdfCaricato.nomeOriginale : ""),
        },
        ...(pdfCaricato && !pdfCaricato.avviso
          ? [{
              chiave: "modelloPdf",
              titolo: "Applica il modello PDF",
              completo: !!modelloPdfApplicato,
              riepilogo: modelloPdfApplicato ? `Modello applicato: ${modelloPdfApplicato.cliente}` : "",
            }]
          : []),
        {
          chiave: "template",
          titolo: "Applica il template",
          completo: !!templateApplicato,
          riepilogo: templateApplicato ? `Template applicato: ${templateApplicato.cliente}` : "",
        },
      ]
    : [];
  // Il primo passo non ancora completato: tutto quello prima è già fatto
  // (si comprime), quello è l'unico aperto di default, tutto quello dopo
  // resta bloccato finché non ci si arriva. -1 = tutti completati.
  const indiceAttivoWizard = passiWizard.findIndex((p) => !p.completo);

  function statoDiPasso(chiave) {
    if (!wizard) return null;
    const i = passiWizard.findIndex((p) => p.chiave === chiave);
    if (i === -1) return null;
    const passo = passiWizard[i];
    const ultimo = i === passiWizard.length - 1;
    // L'ultimo passo resta aperto anche da completato (contiene "Vai alla
    // revisione" e l'anteprima): comprimerlo nasconderebbe l'unico modo di
    // proseguire. Gli altri, da completati, si comprimono di default.
    const espansoDiDefault = i === indiceAttivoWizard || (passo.completo && ultimo);
    return {
      bloccato: indiceAttivoWizard !== -1 && i > indiceAttivoWizard,
      completo: passo.completo,
      espanso: stepRiaperto === chiave || espansoDiDefault,
      riepilogo: passo.riepilogo,
      onToggle: () => setStepRiaperto((prev) => (prev === chiave ? null : chiave)),
    };
  }

  return (
    <div className="pagina">
      <SezionePasso numero={1} titolo="Carica il file del cliente" stato={statoDiPasso("file")}>
        {!modalitaMultipla && (
          <div style={{ marginBottom: "0.5rem" }}>
            <button type="button" disabled={caricamento} onClick={attivaCaricamentoMultiplo}>
              📚 Caricamento multiplo
            </button>
          </div>
        )}
        <div
          className={`zona-upload${trascinamento ? " zona-upload--attiva" : ""}${caricamento ? " zona-upload--disabilitata" : ""}`}
          onClick={() => !caricamento && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); if (!caricamento) setTrascinamento(true); }}
          onDragLeave={() => setTrascinamento(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !caricamento) inputRef.current?.click(); }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.pdf"
            multiple
            onChange={onFileSelezionato}
            disabled={caricamento}
            style={{ display: "none" }}
          />
          <UploadCloud size={30} strokeWidth={1.5} className="zona-upload-icona" />
          <div className="zona-upload-testo">
            <strong>Trascina qui il file, o scegline uno</strong>
            <div className="zona-upload-formati">
              <span className="zona-upload-formato" title="Excel (.xlsx, .xls)"><IconaExcel size={18} /> .xlsx/.xls</span>
              <span className="zona-upload-formato" title="PDF (distinta base stampata)"><IconaPdf size={18} /> .pdf</span>
            </div>
          </div>
          <button
            type="button"
            className="btn-primario zona-upload-bottone"
            disabled={caricamento}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          >
            {caricamento ? (avanzamento || "Caricamento...") : (modalitaMultipla ? "Aggiungi PDF" : "Scegli file")}
          </button>
        </div>
        {modalitaMultipla && (
          <div className="zona-upload-esito" style={{ marginTop: "0.75rem" }}>
            <p className="testo-secondario" style={{ marginTop: 0 }}>
              📚 <strong>Caricamento multiplo</strong>: aggiungi i PDF anche uno per volta. Non succede nulla finché non premi "Vai":
              allora vengono riconosciuti e mappati tutti con lo stesso modello.
            </p>
            {codaMultipla.map((f, i) => (
              <p key={`${f.name}-${f.size}`} className="info-file" style={{ margin: "0.25rem 0" }}>
                <IconaPdf size={18} /> <strong>{f.name}</strong>
                <button
                  type="button"
                  className="btn-link-danger zona-upload-rimuovi"
                  onClick={() => setCodaMultipla((prima) => prima.filter((_, j) => j !== i))}
                  title="Togli questo PDF dalla coda"
                >
                  <X size={14} strokeWidth={2.5} /> Rimuovi
                </button>
              </p>
            ))}
            <div className="riga-controlli" style={{ marginTop: "0.5rem" }}>
              <button
                type="button"
                className="btn-primario"
                disabled={caricamento || codaMultipla.length === 0}
                onClick={avviaCaricamentoMultiplo}
              >
                Vai ({codaMultipla.length} PDF)
              </button>
              <button type="button" disabled={caricamento} onClick={annullaCaricamentoMultiplo}>Annulla</button>
            </div>
          </div>
        )}
        {fileCaricato && (
          <p className="info-file zona-upload-esito">
            {/\.pdf$/i.test(fileCaricato.nomeOriginale) ? <IconaPdf size={18} /> : <IconaExcel size={18} />}
            <strong>{fileCaricato.nomeOriginale}</strong> — {fileCaricato.totaleRighe} righe, {fileCaricato.headers.length} colonne
            <button
              type="button"
              className="btn-link-danger zona-upload-rimuovi"
              onClick={rimuoviFileCaricato}
              title="Rimuovi il file caricato"
            >
              <X size={14} strokeWidth={2.5} /> Rimuovi
            </button>
          </p>
        )}
        {fileCaricato?.colonneAmbigue?.length > 0 && (
          // Rete di sicurezza lato server per quando SceltaIntestazioneModal
          // non è comparso (foglio/riga già noti da un template salvato, o
          // riconoscimento automatico) — vedi i commenti in routes/api.js.
          <div className="errore" style={{ marginTop: "0.5rem" }}>
            ⚠️ {fileCaricato.colonneAmbigue.length} colonna/e di questo file hanno un'intestazione ambigua su due
            righe (un'etichetta di gruppo e il nome specifico nella stessa colonna) — controlla che sia stato scelto
            il nome giusto, non quello generico:
            <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1.2rem" }}>
              {fileCaricato.colonneAmbigue.slice(0, 8).map((c) => (
                <li key={c.colonna}>
                  Colonna {c.colonna}: "{c.principale}" / "{c.specifica}" → usata "{c.risolto}"
                </li>
              ))}
            </ul>
            {fileCaricato.colonneAmbigue.length > 8 && (
              <p style={{ margin: "0.4rem 0 0" }}>...e altre {fileCaricato.colonneAmbigue.length - 8} colonna/e.</p>
            )}
          </div>
        )}
        {fileCaricato?.intestazioniDuplicate?.length > 0 && (
          <p className="errore" style={{ marginTop: "0.5rem" }}>
            ⚠️ Questo file ha colonne con lo stesso nome dopo la lettura dell'intestazione:{" "}
            <strong>{fileCaricato.intestazioniDuplicate.join(", ")}</strong> — i dati della prima vengono sovrascritti
            da quelli dell'ultima con lo stesso nome.
          </p>
        )}
      </SezionePasso>

      {errore && <AvvisoModal messaggio={errore} onChiudi={() => setErrore("")} />}

      <input ref={inputModificaModelloRef} type="file" accept=".pdf" onChange={onFilePerModificaModello} style={{ display: "none" }} />
      <input ref={inputModificaTemplateRef} type="file" accept=".xlsx,.xls" onChange={onFilePerModificaTemplate} style={{ display: "none" }} />

      {fileInAttesaFoglio && (
        <SceltaFoglioModal
          nomeFile={fileInAttesaFoglio.nomeOriginale}
          fogli={fileInAttesaFoglio.fogli}
          onScegli={procediConFoglio}
          onAnnulla={annullaSceltaFoglio}
        />
      )}

      {fileInAttesaIntestazione && (
        <SceltaIntestazioneModal
          nomeFile={fileInAttesaIntestazione.nomeOriginale}
          righeAnteprima={fileInAttesaIntestazione.righeAnteprima}
          onScegli={procediConIntestazione}
          onAnnulla={annullaSceltaIntestazione}
        />
      )}

      {pdfCaricato && !pdfCaricato.avviso && (
        // "titolo" solo in modalità wizard: per chi può mappare questa
        // sezione non ha mai avuto un header numerato, resta così.
        <SezionePasso numero={2} titolo={wizard ? "Applica il modello PDF" : null} stato={statoDiPasso("modelloPdf")}>
          {(lottoPdf ? [pdfCaricato, ...lottoPdf.extra] : [pdfCaricato]).map((p) => (
            <p key={p.pdfId} className="info-file zona-upload-esito" style={{ marginTop: 0 }}>
              <IconaPdf size={18} />
              <strong>{p.nomeOriginale}</strong>
              <button
                type="button"
                className="btn-link-danger zona-upload-rimuovi"
                onClick={() => rimuoviPdfDalLotto(p.pdfId)}
                title="Rimuovi il file caricato"
              >
                <X size={14} strokeWidth={2.5} /> Rimuovi
              </button>
            </p>
          ))}
          {lottoPdf && (
            <div style={{ marginBottom: "0.75rem" }}>
              <p className="testo-secondario" style={{ marginTop: 0 }}>
                📚 Caricamento multiplo: <strong>{1 + lottoPdf.extra.length} PDF</strong> con lo stesso modello.
                I dati da mettere a mano in Revisione valgono per tutti; l'esportazione crea un file per ogni articolo.
              </p>
              {lottoPdf.scartati.length > 0 && (
                <div className="errore">
                  ⚠️ {lottoPdf.scartati.length} PDF lasciati fuori:
                  <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1.2rem" }}>
                    {lottoPdf.scartati.map((s, i) => <li key={i}>{s.nomeOriginale} — {s.motivo}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          {!caricamento && !modelloPdfApplicato && !puoMappare && applicaModelloBloccato && (
            // Chi non può mappare non può calibrare/modificare un modello: se il
            // layout non è riconosciuto al 100% (vedi applicaModelloBloccato più
            // sotto) va detto chiaramente cosa fare, invece di lasciarlo
            // applicare un modello che potrebbe non essere quello giusto.
            // !caricamento evita che compaia per un attimo mentre il
            // riconoscimento sul PDF appena caricato è ancora in corso.
            ticketPdfInviato ? (
              <p style={{ ...STILE_FLAG_APPLICATO, marginTop: 0, marginBottom: "0.75rem" }}>
                ✅ Ticket inviato — attendi la risposta dell'assistenza, o carica un nuovo file.
              </p>
            ) : (
            <div className="errore">
              <p style={{ marginTop: 0, marginBottom: "0.5rem" }}>
                Modello PDF non riconosciuto: non puoi procedere da solo con questo file. Premi "Apri ticket" per
                inviare una richiesta di assistenza a wTrendy, che calibrerà il layout per te.
              </p>
              <button type="button" className="btn-primario" onClick={() => setMostraTicketPdf(true)}>📩 Apri ticket</button>
            </div>
            )
          )}
          {mostraTicketPdf && (
            <ApriTicketModal
              onInviato={() => setTicketPdfInviato(true)}
              modulo={modulo === "diba" ? "Distinta Base" : "DDT"}
              tipoProblema="pdf"
              nomeFile={pdfCaricato?.nomeOriginale}
              pdfId={pdfCaricato?.pdfId}
              azienda={azienda}
              punteggio={suggerimentoModello?.punteggio ?? null}
              onChiudi={() => setMostraTicketPdf(false)}
            />
          )}
          {suggerimentoModello && !modelloPdfApplicato && (
            <p className="testo-secondario">
              📎 Layout riconosciuto come <strong>"{suggerimentoModello.cliente}"</strong> ({Math.round(suggerimentoModello.punteggio * 100)}%
              di corrispondenza) — già selezionato qui sotto, verifica ed applica, o scegline un altro.
            </p>
          )}
          <div className="scelta-mapping">
            {modelliPdf.length > 0 && (
              <div className="scelta-blocco">
                <label>Usa un modello PDF già calibrato</label>
                <div className="riga-controlli">
                  <select value={modelloPdfSceltoEffettivo} onChange={(e) => onCambiaModelloPdfScelto(e.target.value)}>
                    {modelliPdf.map((m) => <option key={m.id} value={m.id}>{m.cliente}</option>)}
                  </select>
                  <button
                    className="btn-primario"
                    disabled={caricamento || applicaModelloBloccato}
                    onClick={usaModelloPdfScelto}
                    title={applicaModelloBloccato ? "Il layout non è riconosciuto al 100%: contatta l'assistenza per calibrare o correggere il modello" : undefined}
                  >
                    {modelloPdfApplicato ? "Applica di nuovo" : "Applica modello"}
                  </button>
                  {puoMappare && (
                    <button disabled={caricamento} onClick={modificaModelloPdfScelto}>
                      Modifica modello
                    </button>
                  )}
                </div>
              </div>
            )}
            {puoMappare && (
              <div className="scelta-blocco">
                <label>Oppure calibra un nuovo modello per questo PDF</label>
                <button disabled={caricamento} onClick={calibraNuovoModelloPdf}>Calibra nuovo modello</button>
              </div>
            )}
          </div>
          {modelloPdfApplicato && (
            <p className="testo-secondario" style={{ ...STILE_FLAG_APPLICATO, marginTop: "0.75rem", marginBottom: 0 }}>
              ✅ Modello applicato: {modelloPdfApplicato.cliente}
              {fileCaricato && (lottoPdf?.file
                ? ` — ${1 + lottoPdf.file.length} PDF, ${fileCaricato.totaleRighe + lottoPdf.file.reduce((s, f) => s + f.totaleRighe, 0)} righe lette in totale, ${fileCaricato.headers.length} colonne.`
                : ` — ${fileCaricato.totaleRighe} righe lette, ${fileCaricato.headers.length} colonne.`)}
              {" "}Scegli come mappare qui sotto, oppure applica un modello diverso qui sopra.
            </p>
          )}
        </SezionePasso>
      )}

      {/* In modalità wizard questa sezione resta visibile anche PRIMA di
          caricare un file (mostra il lucchetto "completa il passo
          precedente", come in OrdiniMapping.jsx) invece di sparire del
          tutto: fa vedere subito tutti i passi del percorso, non solo quello
          attivo. Per chi può mappare invece resta come sempre — compare solo
          dopo aver caricato un file, nessun cambiamento per lui. */}
      {(fileCaricato || wizard) && (
        <SezionePasso
          numero={wizard ? passiWizard.findIndex((p) => p.chiave === "template") + 1 : 2}
          titolo="Scegli come mappare"
          stato={statoDiPasso("template")}
        >
          {!caricamento && !templateApplicato && !puoMappare && applicaTemplateBloccato && (
            // Stessa idea del blocco sui modelli PDF più sopra: chi non può
            // mappare non può correggere un template sbagliato, quindi non può
            // applicarne uno riconosciuto solo in parte o scelto a mano.
            // !caricamento evita che compaia per un attimo mentre il
            // riconoscimento sulle colonne del file appena caricato è ancora
            // in corso.
            ticketTemplateInviato ? (
              <p style={{ ...STILE_FLAG_APPLICATO, marginTop: 0, marginBottom: "0.75rem" }}>
                ✅ Ticket inviato — attendi la risposta dell'assistenza, o carica un nuovo file.
              </p>
            ) : (
            <div className="errore">
              <p style={{ marginTop: 0, marginBottom: "0.5rem" }}>
                Template non riconosciuto: non puoi procedere da solo con questo file. Premi "Apri ticket" per
                inviare una richiesta di assistenza a wTrendy, che mapperà il file per te.
              </p>
              <button type="button" className="btn-primario" onClick={() => setMostraTicketTemplate(true)}>📩 Apri ticket</button>
            </div>
            )
          )}
          {mostraTicketTemplate && (
            <ApriTicketModal
              onInviato={() => setTicketTemplateInviato(true)}
              modulo={modulo === "diba" ? "Distinta Base" : "DDT"}
              tipoProblema="template"
              nomeFile={fileCaricato?.nomeOriginale}
              fileGrezzo={fileOriginaleGrezzo}
              azienda={azienda}
              punteggio={suggerimentoTemplate?.punteggio ?? null}
              onChiudi={() => setMostraTicketTemplate(false)}
            />
          )}
          {suggerimentoTemplate && !templateApplicato && (
            <p className="testo-secondario">
              📎 Colonne riconosciute come template <strong>"{suggerimentoTemplate.cliente}"</strong>{" "}
              ({Math.round(suggerimentoTemplate.punteggio * 100)}% di corrispondenza) — già selezionato qui sotto,
              verifica ed applica, o scegline un altro.
            </p>
          )}
          <div className="scelta-mapping">
            <div className="scelta-blocco">
              <label>Usa un template cliente già salvato</label>
              <div className="riga-controlli">
                <select value={templateSelezionatoEffettivo} onChange={(e) => onCambiaTemplateSelezionato(e.target.value)}>
                  <option value="">-- seleziona cliente --</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.cliente}</option>
                  ))}
                </select>
                <button
                  disabled={!templateSelezionatoEffettivo || caricamento || applicaTemplateBloccato}
                  onClick={usaTemplateEsistente}
                  title={applicaTemplateBloccato ? "Le colonne non sono riconosciute al 100%: contatta l'assistenza per creare o correggere il template" : undefined}
                >
                  {templateApplicato ? "Applica di nuovo" : "Applica template"}
                </button>
                {puoMappare && (
                  <button disabled={!templateSelezionatoEffettivo || caricamento} onClick={() => modificaTemplate(templateSelezionatoEffettivo)}>
                    Modifica mapping
                  </button>
                )}
              </div>
            </div>
            {puoMappare && (
              <div className="scelta-blocco">
                <label>Oppure crea un nuovo mapping per questo file</label>
                <button onClick={creaNuovoMapping}>Crea nuovo mapping</button>
              </div>
            )}
          </div>
          {templateApplicato && (
            <>
              {/* In modalità wizard il checkmark verde "Template applicato" è
                  già nell'intestazione della sezione (SezionePasso): qui basta
                  il pulsante per proseguire, ripetere lo stesso messaggio
                  sarebbe ridondante. */}
              {wizard ? (
                <div className="riga-controlli" style={{ marginTop: "0.75rem", justifyContent: "flex-end" }}>
                  <button className="btn-primario" onClick={() => navigate(`${percorsoBase}/revisione`)}>
                    Vai alla revisione →
                  </button>
                </div>
              ) : (
                <div className="riga-controlli" style={{ ...STILE_FLAG_APPLICATO, marginTop: "0.75rem", justifyContent: "space-between" }}>
                  <span>
                    ✅ Template applicato: {templateApplicato.cliente} — pronte per la revisione.
                    Controlla qui sopra se è quello giusto, o applicane un altro.
                  </span>
                  <button className="btn-primario" onClick={() => navigate(`${percorsoBase}/revisione`)}>
                    Vai alla revisione →
                  </button>
                </div>
              )}
              {righeMappate && righeMappate.length > 0 && (
                <div style={{ marginTop: "1rem" }}>
                  <p className="testo-secondario" style={{ marginBottom: "0.4rem" }}>
                    Anteprima — {righeMappate.length} righe:
                  </p>
                  <div className="scroll-orizzontale scroll-verticale">
                    <table className="tabella-anteprima">
                      <thead>
                        <tr>{colonneAnteprima.map((c) => <th key={c.chiave}>{c.nome}</th>)}</tr>
                      </thead>
                      <tbody>
                        {righeMappate.map((riga, i) => (
                          <tr key={i}>{colonneAnteprima.map((c) => <td key={c.chiave}>{String(riga[c.chiave] ?? "")}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </SezionePasso>
      )}

      <section className="riquadro">
        <h2>Template clienti salvati</h2>
        {puoMappare && (
          <p className="testo-secondario">
            Per modificare un template serve un file di riferimento (con le stesse colonne) da guardare durante la modifica.
          </p>
        )}
        {templates.length === 0 && <p className="testo-secondario">Nessun template salvato ancora.</p>}
        {templates.length > 0 && (
          <table className="tabella-elenco">
            <thead>
              <tr><th>Cliente</th><th>Aggiornato</th>{puoMappare && (<><th></th><th></th></>)}</tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td>{t.cliente}</td>
                  <td>{new Date(t.aggiornato).toLocaleString("it-IT")}</td>
                  {puoMappare && (
                    <>
                      <td><button onClick={() => chiediFilePerModificaTemplate(t.id)} disabled={caricamento}>Modifica</button></td>
                      <td><button className="btn-link-danger" onClick={() => rimuoviTemplate(t.id)}>Elimina</button></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="riquadro">
        <h2>Modelli PDF salvati</h2>
        {puoMappare && (
          <p className="testo-secondario">
            Per modificare un modello serve un PDF di riferimento (con lo stesso layout) da guardare durante la calibrazione.
          </p>
        )}
        {modelliPdfSalvati.length === 0 && <p className="testo-secondario">Nessun modello PDF salvato ancora.</p>}
        {modelliPdfSalvati.length > 0 && (
          <table className="tabella-elenco">
            <thead>
              <tr><th>Cliente</th><th>Aggiornato</th>{puoMappare && (<><th></th><th></th></>)}</tr>
            </thead>
            <tbody>
              {modelliPdfSalvati.map((m) => (
                <tr key={m.id}>
                  <td>{m.cliente}</td>
                  <td>{new Date(m.aggiornato).toLocaleString("it-IT")}</td>
                  {puoMappare && (
                    <>
                      <td><button onClick={() => chiediFilePerModificaModello(m.id)} disabled={caricamento}>Modifica</button></td>
                      <td><button className="btn-link-danger" onClick={() => eliminaModelloPdfSalvato(m.id)}>Elimina</button></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
